import React, { FC, createContext, ReactNode, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Toast, ToastButtonProps, ToastProps } from '../../molecules/Toast';
import { BasicContainer } from '../../atoms';

export type AlertTypes =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'paywallError'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

export type ShowMessageParams = {
  /** what color the notification is themed in, usually "success" or "danger" */
  color?: AlertTypes;
  delay?: number;
  /** the message to show in the notification */
  message: ReactNode;
  /** @deprecated this value no longer does anything and can be removed */
  sticky?: boolean;
  dataQaId?: string;
  /** optional title for the notification */
  title?: string;
  /** optional buttons to show in the notification */
  buttons?: ToastButtonProps[];
};

export type NotificationContextType = {
  /**
   * function to invoke and display a toast message
   * @param message - the message to display (can be a string or ReactNode)
   * @param color - what theme the toast should be themed in ("danger" for errors, "success" for success), defaults to 'success'
   *
   * @example
   * showMessage({ message: 'This is a message', color: 'success' }) // success toast message
   * showMessage({ message: 'This is a message', color: 'danger' }) // error toast message
   */
  showMessage?: (params: ShowMessageParams) => void;
};

// Context is used to store only 1 single event no state should be stored
const NotificationContext = createContext<NotificationContextType>({});

const defaultParams: ShowMessageParams = {
  color: 'success',
  message: '',
  sticky: false,
};

const verifyColorPropForToast = (color: AlertTypes): ToastProps['variant'] => {
  switch (color) {
    case 'success':
      return 'success';
    case 'danger':
    case 'paywallError':
      return 'danger';
    case 'warning':
      return 'warning';
    default:
      return 'success';
  }
};

export const NotificationProvider: FC = ({ children }) => {
  const [messageParams, setMessageParams] = useState<ShowMessageParams>(defaultParams);
  const handleClose = useCallback(() => {
    setMessageParams(defaultParams);
  }, []);

  useEffect(() => {
    const hasMessage = Boolean(messageParams?.message);

    if (hasMessage) {
      const delay = messageParams?.delay || 3000;
      const timeoutId = window.setTimeout(handleClose, delay);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return () => undefined;
  }, [messageParams, handleClose]);

  const showMessage = useCallback((params: ShowMessageParams) => {
    setMessageParams({
      ...defaultParams,
      ...params,
    });
  }, []);

  const { color, message, dataQaId, buttons, title } = messageParams;
  const { contextValue } = useMemo(() => ({ contextValue: { showMessage } }), [showMessage]);

  return (
    <>
      {Boolean(message) && (
        <BasicContainer className="cn_fixed cn_atom_zIndex-9999999" bottom={1} left={1}>
          <Toast
            handleClose={handleClose}
            description={message}
            variant={verifyColorPropForToast(color)}
            data-qa-id={dataQaId}
            data-testid="global-notification-alert"
            title={title}
            buttons={buttons}
          />
        </BasicContainer>
      )}
      <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
    </>
  );
};

/*
 * Used to show a global notification
 */
export const useGlobalNotification = () => {
  const context = useContext(NotificationContext);

  if (!context) throw new Error('useGlobalNotification must be used within the NotificationProvider');

  return context;
};
